import { RTC_MESSAGE, type RtcSignal } from "@spacecrew/shared";
import type { ShipState } from "@spacecrew/shared/schema";
import type { Room } from "@colyseus/sdk";
import { audio } from "../audio/bus.js";
import { send } from "../net/intents.js";
import { session } from "../net/session.svelte.js";
import { ship } from "../net/ship.svelte.js";
import { intercom } from "./store.svelte.js";
import { radio } from "./radio.js";

/**
 * Full-mesh WebRTC: one RTCPeerConnection per other crew member. The server only relays
 * `rtc.signal`; no media ever touches it. "Perfect negotiation" (the peer with the lexically
 * greater sessionId is polite), so both sides can renegotiate when tracks change.
 */
interface Peer { pc: RTCPeerConnection; polite: boolean; makingOffer: boolean; ignoreOffer: boolean; senders: RTCRtpSender[]; since: number; remoteEpoch: string }
/** Identifies this page load. A signal from a peer with a different epoch than before = they have a fresh page. */
const EPOCH = Math.random().toString(36).slice(2, 10);
const STALL_MS = 10_000;

/** ICE config comes from the server (GET /ice) so STUN/TURN can change per deployment without a rebuild. */
const ICE: RTCConfiguration = { iceServers: [] };
// ?relay=1 forces every connection through TURN — a diagnostic for "is the relay actually working".
if (new URLSearchParams(location.search).has("relay")) ICE.iceTransportPolicy = "relay";
let iceLoaded: Promise<void> | null = null;
function loadIce(): Promise<void> {
  return (iceLoaded ??= fetch("/ice").then((r) => r.json()).then((j: { iceServers?: RTCIceServer[] }) => {
    ICE.iceServers = j.iceServers ?? [];
    if (DEBUG) console.debug("[mesh] ice servers", ICE.iceServers.map((s) => s.urls));
  }).catch(() => { /* host candidates only */ }));
}
const MAX_VIDEO_KBPS = 80;
const DEBUG = import.meta.env.DEV || new URLSearchParams(location.search).has("debug");

class Mesh {
  private peers = new Map<string, Peer>();
  private connected = new Map<string, boolean>();
  private room: Room<any, ShipState> | null = null;
  private local: MediaStream | null = null;
  private effectStarted = false;
  private watchdog = 0;
  private dialedInitial = false;

  attach(room: Room<any, ShipState>) {
    this.room = room;
    void loadIce();
    room.onMessage(RTC_MESSAGE, (m: RtcSignal) => { void this.onSignal(m); });
    // Watchdog: signaling is best-effort (a message can arrive before a fresh page listens). The
    // impolite side restarts any peer that has not connected within STALL_MS.
    if (!this.watchdog) this.watchdog = window.setInterval(() => {
      const now = performance.now();
      for (const [id, p] of this.peers) {
        if (p.pc.connectionState !== "connected" && now - p.since > STALL_MS && !p.polite && this.connected.get(id)) {
          if (DEBUG) console.debug("[mesh] stalled, re-dialing", id.slice(-4), p.pc.connectionState);
          this.drop(id, true); this.connect(id);
        }
      }
    }, 2500);
    if (!this.effectStarted) {
      this.effectStarted = true;
      $effect.root(() => {
        // Crew roster → peer set. Only peers who have opened their intercom (or we have) are dialed.
        $effect(() => {
          const snap = ship.snap;
          if (!snap || !session.sessionId) return;
          const ids = Object.keys(snap.crew).filter((id) => id !== session.sessionId);
          for (const id of ids) this.connected.set(id, snap.crew[id]!.connected);
          // Only the newcomer dials: a page dials everyone on its first snapshot (their handlers are
          // live), then waits to be dialed by anyone who joins or refreshes later (new epoch → new pc).
          if (!this.dialedInitial) {
            this.dialedInitial = true;
            void Promise.race([loadIce(), new Promise((r) => setTimeout(r, 1500))]).then(() => { for (const id of ids) if (!this.peers.has(id)) this.connect(id); });
          }
          for (const id of [...this.peers.keys()]) if (!ids.includes(id)) { this.drop(id, true); this.connected.delete(id); }
        });
      });
    }
  }

  setLocalStream(stream: MediaStream | null) {
    this.local = stream;
    for (const [id, p] of this.peers) this.syncTracks(id, p);
  }

  private connect(id: string) {
    if (!ICE.iceServers?.length && iceLoaded) { /* first dial may race /ice; the watchdog re-dials with servers if needed */ }
    const pc = new RTCPeerConnection(ICE);
    const peer: Peer = { pc, polite: session.sessionId > id, makingOffer: false, ignoreOffer: false, senders: [], since: performance.now(), remoteEpoch: "" };
    this.peers.set(id, peer);
    intercom.setPeer(id, { state: "new" });

    pc.onnegotiationneeded = async () => {
      try {
        peer.makingOffer = true;
        await pc.setLocalDescription();
        this.signal(id, { description: pc.localDescription!.toJSON() as { type: RTCSdpType; sdp?: string } });
      } catch (e) { console.warn("[mesh] negotiation failed", e); }
      finally { peer.makingOffer = false; }
    };
    pc.onicecandidate = ({ candidate }) => {
      this.signal(id, { candidate: candidate ? candidate.toJSON() as { candidate: string; sdpMid?: string | null; sdpMLineIndex?: number | null; usernameFragment?: string | null } : null });
    };
    pc.onconnectionstatechange = () => {
      if (DEBUG) console.debug("[mesh] state", id.slice(-4), pc.connectionState, "ice:", pc.iceConnectionState, "sig:", pc.signalingState);
      intercom.setPeer(id, { state: pc.connectionState });
      if (pc.connectionState === "connected") audio.play("intercom.peer.link");
      if (pc.connectionState === "failed") { audio.play("intercom.peer.lost"); pc.restartIce(); }
    };
    pc.ontrack = ({ track, streams }) => {
      const stream = streams[0] ?? new MediaStream([track]);
      intercom.setPeer(id, { stream, hasVideo: stream.getVideoTracks().length > 0, hasAudio: stream.getAudioTracks().length > 0 });
      track.onunmute = () => intercom.setPeer(id, { hasVideo: stream.getVideoTracks().some((t) => !t.muted), hasAudio: stream.getAudioTracks().some((t) => !t.muted) });
      if (track.kind === "audio") radio.attach(id, stream);
    };
    this.syncTracks(id, peer);
  }

  /** Make the connection carry exactly our current local tracks (replace in place when possible). */
  private syncTracks(id: string, peer: Peer) {
    const tracks = this.local?.getTracks() ?? [];
    for (const track of tracks) {
      const existing = peer.senders.find((s) => s.track?.kind === track.kind);
      if (existing) { void existing.replaceTrack(track); continue; }
      const sender = peer.pc.addTrack(track, this.local!);
      peer.senders.push(sender);
      if (track.kind === "video") {
        const params = sender.getParameters();
        params.encodings = [{ maxBitrate: MAX_VIDEO_KBPS * 1000, maxFramerate: 12 }];
        void sender.setParameters(params).catch(() => {});
      }
    }
    for (const sender of peer.senders) {
      if (sender.track && !tracks.includes(sender.track)) void sender.replaceTrack(null);
    }
    if (!tracks.length && !peer.senders.length) {
      // Receive-only until we open our own intercom: still negotiate so we can see/hear others.
      if (peer.pc.getTransceivers().length === 0) { peer.pc.addTransceiver("video", { direction: "recvonly" }); peer.pc.addTransceiver("audio", { direction: "recvonly" }); }
    }
  }

  private signal(to: string, payload: RtcSignal["payload"]) { if (DEBUG) console.debug("[mesh] →", to.slice(-4), payload.description?.type ?? (payload.candidate === null ? "end-of-candidates" : "candidate")); send("rtc.signal", { to, epoch: EPOCH, payload }); }

  private async onSignal({ from, epoch, payload }: RtcSignal) {
    if (DEBUG) console.debug("[mesh] ←", from.slice(-4), epoch, payload.description?.type ?? (payload.candidate === null ? "end-of-candidates" : "candidate"));
    let peer = this.peers.get(from);
    // A new epoch = the other side reloaded. Their old connection is unusable (new DTLS identity): start over.
    if (peer && peer.remoteEpoch && peer.remoteEpoch !== epoch) { this.drop(from, true); peer = undefined; }
    if (!peer) { this.connect(from); peer = this.peers.get(from)!; }
    peer.remoteEpoch = epoch;
    const { pc } = peer;
    try {
      if (payload.description) {
        const desc = payload.description as RTCSessionDescriptionInit;
        const collision = desc.type === "offer" && (peer.makingOffer || pc.signalingState !== "stable");
        peer.ignoreOffer = !peer.polite && collision;
        if (peer.ignoreOffer) return;
        await pc.setRemoteDescription(desc);
        if (desc.type === "offer") {
          await pc.setLocalDescription();
          this.signal(from, { description: pc.localDescription!.toJSON() as { type: RTCSdpType; sdp?: string } });
        }
      } else if (payload.candidate !== undefined) {
        try { await pc.addIceCandidate(payload.candidate ?? undefined); }
        catch (e) { if (!peer.ignoreOffer) throw e; }
      }
    } catch (e) { console.warn("[mesh] signal error", e); }
  }

  private drop(id: string, silent = false) {
    const peer = this.peers.get(id);
    if (!peer) return;
    peer.pc.close();
    this.peers.delete(id);
    radio.detach(id);
    intercom.removePeer(id);
    if (!silent) audio.play("intercom.peer.lost");
  }

  closeAll() { for (const id of [...this.peers.keys()]) this.drop(id, true); }
}

export const mesh = new Mesh();
